import type { FormEvent, FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button, ButtonGroup, Form } from '@george-labs.com/design-system';

import {
    clearFeaturesStorage,
    getGatekeeperDefaultFeatures,
    getGatekeeperFeatures,
    getGatekeeperFeaturesWithoutCustomOverrides,
    setFeaturesToStorage,
} from '@luna/gatekeeper';
import { useNotifications } from '@shared/snackbar';

import type { ActiveFormFeatures } from './types';
import { FeatureList } from './FeatureList';
import { FeatureListSearchInput } from './FeatureListSearchInput';
import {
    convertFeatureMapToArray,
    filterFeatures,
    flattenFeatures,
    getChangedFeatures,
    mapFeaturesToFormData,
    nestFeatures,
} from './FeatureToggleFormService';

export const FeatureToggleForm: FunctionComponent = () => {
    const { displaySuccess, displayError } = useNotifications('dev-tools-dock');

    const [searchTerm, setSearchTerm] = useState('');

    const formFeatures = useMemo(() => {
        const features = flattenFeatures(getGatekeeperFeatures());
        const featuresWithoutCustomOverrides = flattenFeatures(
            getGatekeeperFeaturesWithoutCustomOverrides(),
        );
        return mapFeaturesToFormData(features, featuresWithoutCustomOverrides);
    }, []);

    const filteredFeatureArray = useMemo(() => {
        const filteredFeatureMap = filterFeatures(formFeatures, searchTerm);
        return convertFeatureMapToArray(filteredFeatureMap);
    }, [formFeatures, searchTerm]);

    const handleSearchChange = useCallback(
        (updatedSearchTerm: string) => setSearchTerm(updatedSearchTerm),
        [],
    );

    const handleCopyDefaults = useCallback(async () => {
        const defaultFeatures = flattenFeatures(getGatekeeperDefaultFeatures());
        try {
            await navigator?.clipboard.writeText(JSON.stringify(defaultFeatures));
            displaySuccess('Default flags copied to clipboard.');
        } catch (e) {
            displayError(`Default flags copy error: ${e}`);
        }
    }, [displaySuccess, displayError]);

    const handleResetToDefault = useCallback(() => {
        clearFeaturesStorage();
        window.location.reload();
    }, []);

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const activeFeatures = Object.fromEntries(
                new FormData(event.currentTarget),
            ) as ActiveFormFeatures;
            const changedFeatures = getChangedFeatures(formFeatures, activeFeatures);
            const nestedChangedFeatures = nestFeatures(changedFeatures);
            setFeaturesToStorage(nestedChangedFeatures);
            window.location.reload();
        },
        [formFeatures],
    );

    return (
        <Form onSubmit={handleSubmit}>
            <FeatureListSearchInput value={searchTerm} onChange={handleSearchChange} />
            <FeatureList features={filteredFeatureArray} />
            <ButtonGroup className="text-center">
                <Button variant={Button.VARIANT.SECONDARY} onClick={handleCopyDefaults}>
                    Copy defaults
                </Button>
                <Button variant={Button.VARIANT.SECONDARY} type="reset">
                    Reset unsaved
                </Button>
                <Button variant={Button.VARIANT.DANGER} onClick={handleResetToDefault}>
                    Apply defaults
                </Button>
                <Button variant={Button.VARIANT.PRIMARY} type="submit">
                    Apply changes
                </Button>
            </ButtonGroup>
        </Form>
    );
};
